

import type React from "react"
import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Check, Pencil, X } from "lucide-react"
import { cn } from "@/lib/utils"
import type { GTINProduct } from "./product-quantity-checker"

interface ProductItemProps {
  product: GTINProduct
  onQuantityChange: (quantity: number) => void
}

export function ProductItem({ product, onQuantityChange }: ProductItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(product.qntd.toString())

  const handleSave = () => {
    const value = inputValue.trim()
    const num = Number.parseInt(value, 10)
    if (!isNaN(num) && num >= 0) {
      onQuantityChange(num)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setInputValue(product.qntd.toString())
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave()
    } else if (e.key === "Escape") {
      handleCancel()
    }
  }

  const handleStartEdit = () => {
    setInputValue(product.qntd.toString())
    setIsEditing(true)
  }

  return (
    <Card className={cn("transition-all duration-200", product.isEdited && "border-success/50 bg-success/5")}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          {/* Product Info */}
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{product.xProd}</h3>
              {product.isEdited && (
                <Badge className="bg-success text-success-foreground gap-1">
                  <Check className="h-3 w-3" />
                  Editado
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>
                <span className="font-medium">EAN:</span> {product.cEAN}
              </span>
              <span>
                <span className="font-medium">NCM:</span> {product.ncm}
              </span>
              <span>
                <span className="font-medium">uCom:</span> {product.uCom}
              </span>
              {product.cest && (
                <span>
                  <span className="font-medium">CEST:</span> {product.cest}
                </span>
              )}
            </div>
          </div>

          {/* Quantity Input */}
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-28 text-center"
                    placeholder="0"
                    autoFocus
                  />
                  <span className="text-sm text-muted-foreground">UN</span>
                </div>
                <Button size="icon" variant="ghost" onClick={handleSave} className="text-success hover:text-success">
                  <Check className="h-4 w-4" />
                  <span className="sr-only">Salvar</span>
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleCancel}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Cancelar</span>
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 rounded-lg border bg-muted/50 px-4 py-2 min-w-[120px] justify-center">
                  <span className={cn("font-semibold", product.qntd === 0 && "text-muted-foreground")}>
                    {product.qntd} UN
                  </span>
                </div>
                <Button size="icon" variant="outline" onClick={handleStartEdit} className="shrink-0 bg-transparent">
                  <Pencil className="h-4 w-4" />
                  <span className="sr-only">Editar quantidade</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
